/**
 * This file provides a utility class for interacting with Cloudflare's Turnstile challenge, a CAPTCHA-like system for verifying user interactions.
 */
declare global {
  interface Window {
    turnstile: any
  }
}

export class TurnstileChallenge {
  private siteKey: string
  private token: string | null = null
  private containerId: string
  private widgetId: string | null = null
  private pending: {
    resolve: (token: string) => void
    reject: (error: Error) => void
    timeoutId: number | null
  } | null = null

  /**
   * Initializes a new instance of the TurnstileChallenge class with the provided site key.
   * @param siteKey The site key for the Turnstile challenge.
   * @param containerId Optional custom ID for the container. Defaults to 'cf-turnstile-container'.
   */
  constructor(siteKey?: string, containerId: string = 'cf-turnstile-container') {
    const resolvedSiteKey = siteKey ?? import.meta.env.VITE_TURNSTILE_SITE_KEY

    if (!resolvedSiteKey) {
      throw new Error(
        'Turnstile site key is not defined. Please provide it as a parameter or ensure VITE_TURNSTILE_SITE_KEY is set.'
      )
    }

    this.siteKey = resolvedSiteKey
    this.containerId = containerId
  }

  /**
   * Creates a div element to hold the Turnstile widget if it doesn't exist yet.
   * @returns The selector for the created container.
   */
  private createContainer(): string {
    // Check if container already exists
    let container = document.getElementById(this.containerId)

    if (!container) {
      container = document.createElement('div')
      container.id = this.containerId
      document.body.prepend(container)
    }

    const selector = `#${this.containerId}`
    return selector
  }

  /**
   * Loads the Turnstile script dynamically, if it has not already been loaded.
   * @returns A promise that resolves when the script has been loaded, or immediately if the script is already loaded.
   */
  private async loadTurnstileScript(): Promise<void> {
    if (window.turnstile) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      // Explicit rendering since we programmatically render/execute
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = () => {
        resolve()
      }
      script.onerror = error => {
        reject(error)
      }
      document.head.appendChild(script)
    })
  }

  /**
   * Ensures the widget exists and returns a fresh token.
   * If the widget is already rendered, it resets + executes to produce a new token.
   * @param customSelector Optional CSS selector to override the default container.
   * @returns A promise that resolves with a fresh generated token.
   */
  private async requestFreshToken(customSelector?: string): Promise<string> {
    if (typeof window === 'undefined') {
      throw new Error('Turnstile not available in this environment')
    }

    if (typeof window.turnstile === 'undefined') {
      throw new Error('Turnstile script not loaded')
    }

    // If already rendered, reset and execute to obtain a brand new token
    if (this.widgetId) {
      // Clear any previous token before generating a new one
      this.token = null

      return new Promise((resolve, reject) => {
        // Clear any previous pending waiter
        if (this.pending && this.pending.timeoutId) {
          clearTimeout(this.pending.timeoutId)
        }
        this.pending = {
          resolve,
          reject,
          timeoutId: window.setTimeout(() => {
            this.pending = null
            reject(new Error('Turnstile challenge timed out'))
          }, 30000),
        }

        try {
          window.turnstile.reset(this.widgetId)
        } catch (e) {
          // ignore reset errors; execute will still try to obtain a token
        }
        try {
          window.turnstile.execute(this.widgetId)
        } catch (e) {
          // If execute isn't supported, we rely on the widget auto-callback
        }
      })
    }

    // Not yet rendered: render invisibly and execute
    const selector = customSelector || this.createContainer()
    return new Promise((resolve, reject) => {
      // Clear any previous pending waiter
      if (this.pending && this.pending.timeoutId) {
        clearTimeout(this.pending.timeoutId)
      }
      this.pending = {
        resolve,
        reject,
        timeoutId: window.setTimeout(() => {
          this.pending = null
          reject(new Error('Turnstile challenge timed out'))
        }, 30000),
      }

      this.widgetId = window.turnstile.render(selector, {
        sitekey: this.siteKey,
        size: 'invisible',
        callback: (token: string) => this.handleSuccess(token),
        'error-callback': (error: Error) => this.handleError(error),
      })

      try {
        window.turnstile.execute(this.widgetId)
      } catch (e) {
        // If execute isn't supported, rely on automatic flow
      }
    })
  }

  private handleSuccess(token: string) {
    if (!token || token.trim() === '') {
      // Treat empty token as error
      this.handleError(new Error('Invalid Turnstile token received'))
      return
    }
    this.token = token
    if (this.pending) {
      if (this.pending.timeoutId) clearTimeout(this.pending.timeoutId)
      const r = this.pending.resolve
      this.pending = null
      r(token)
    }
  }

  private handleError(error: any) {
    this.widgetId = this.widgetId // keep widgetId; allow retry via reset/execute
    if (this.pending) {
      if (this.pending.timeoutId) clearTimeout(this.pending.timeoutId)
      const rej = this.pending.reject
      this.pending = null
      rej(new Error(`Turnstile failed: ${error?.message || error}`))
    }
  }

  /**
   * Initializes the entire Turnstile challenge flow - loads script, creates container, and renders widget.
   * @param customSelector Optional CSS selector to override the default container.
   * @returns A promise that resolves with the generated token when the flow is complete.
   */
  public async initialize(customSelector?: string): Promise<string> {
    try {
      await this.loadTurnstileScript()
      const token = await this.requestFreshToken(customSelector)
      if (token) {
        return token
      } else {
        throw new Error('Failed to obtain Turnstile token.')
      }
    } catch (error) {
      console.error('[Turnstile] Error in initialize:', error)
      throw error
    }
  }

  /**
   * Retrieves the generated token from the Turnstile challenge, if one has been generated.
   * @returns The generated token, or null if no token has been generated.
   */
  public getToken(): string | null {
    return this.token
  }
}
